import React, { useEffect, useState, useCallback } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { X, User, Shield, Menu, Activity, Check, Loader2, LayoutDashboard, Inbox, ListChecks, FolderOpen, Tags, Users, Building2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

// Arborescence des pages de l'application (route → label)
const MENU_TREE = [
  { path: "/", label: "Tableau de bord", icon: LayoutDashboard },
  { path: "/sas", label: "SAS d'entrée", icon: Inbox },
  { path: "/queue", label: "File de classement", icon: ListChecks },
  { path: "/docs", label: "Explorateur de documents", icon: FolderOpen },
  { path: "/types", label: "Types de documents", icon: Tags },
  { path: "/contacts", label: "Contacts Bedee", icon: Users },
];

const TABS = [
  { key: "profil", label: "Profil", icon: User },
  { key: "acces", label: "Accès", icon: Building2 },
  { key: "menu", label: "Droits Menu", icon: Menu },
  { key: "connexions", label: "Connexions", icon: Activity },
];

function formatDate(iso) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  }).format(new Date(iso));
}

function parseUA(ua) {
  if (!ua) return "—";
  if (ua.includes("Chrome")) return "Chrome";
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("Safari")) return "Safari";
  if (ua.includes("Edge")) return "Edge";
  return ua.slice(0, 40);
}

export default function UserPermissionsPanel({ user, onClose, onSaved }) {
  const { user: currentUser } = useAuth();
  const [tab, setTab] = useState("profil");
  const [permissions, setPermissions] = useState(null);
  const [societes, setSocietes] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loadingPerms, setLoadingPerms] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [saving, setSaving] = useState(false);

  // Profile edit state
  const [editName, setEditName] = useState(user.name || "");
  const [editRole, setEditRole] = useState(user.role || "user");
  const [savingProfile, setSavingProfile] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const isSelf = currentUser?.id === user.id;

  // États des permissions (locaux, non encore sauvegardés)
  const [routeRestricted, setRouteRestricted] = useState(false);
  const [allowedRoutes, setAllowedRoutes] = useState([]);
  const [societeRestricted, setSocieteRestricted] = useState(false);
  const [allowedSocieteIds, setAllowedSocieteIds] = useState([]);

  // Charger permissions + sociétés
  const loadPermissions = useCallback(async () => {
    if (!user) return;
    setLoadingPerms(true);
    try {
      const [permsRes, socRes] = await Promise.all([
        api.get(`/admin/users/${user.id}/permissions`),
        api.get("/societes"),
      ]);
      const p = permsRes.data;
      setSocietes(socRes.data);
      setPermissions(p);
      // Initialiser les états locaux
      setRouteRestricted(Array.isArray(p.allowed_routes));
      setAllowedRoutes(Array.isArray(p.allowed_routes) ? p.allowed_routes : []);
      setSocieteRestricted(Array.isArray(p.allowed_societe_ids));
      setAllowedSocieteIds(Array.isArray(p.allowed_societe_ids) ? p.allowed_societe_ids : []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingPerms(false);
    }
  }, [user]);

  // Charger l'historique des connexions (uniquement quand l'onglet est actif)
  const loadHistory = useCallback(async () => {
    if (!user) return;
    setLoadingHistory(true);
    try {
      const r = await api.get(`/admin/users/${user.id}/login-history`);
      setLoginHistory(r.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingHistory(false);
    }
  }, [user]);

  useEffect(() => { loadPermissions(); }, [loadPermissions]);
  useEffect(() => { if (tab === "connexions") loadHistory(); }, [tab, loadHistory]);

  const toggleRoute = (path) => {
    setAllowedRoutes(prev =>
      prev.includes(path) ? prev.filter(r => r !== path) : [...prev, path]
    );
  };

  const toggleSociete = (id) => {
    setAllowedSocieteIds(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${user.id}/permissions`, {
        allowed_routes: routeRestricted ? allowedRoutes : null,
        allowed_societe_ids: societeRestricted ? allowedSocieteIds : null,
      });
      toast.success("Permissions enregistrées.");
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put(`/admin/users/${user.id}`, {
        name: editName.trim() || null,
        role: editRole,
      });
      toast.success("Profil mis à jour.");
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSavingProfile(false);
    }
  };

  const deleteUser = async () => {
    if (!window.confirm(`Supprimer définitivement le compte de ${user.name || user.email} ?`)) return;
    setDeletingUser(true);
    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success("Utilisateur supprimé.");
      onClose();
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDeletingUser(false);
    }
  };

  const hasChanges = permissions && (
    routeRestricted !== Array.isArray(permissions.allowed_routes) ||
    societeRestricted !== Array.isArray(permissions.allowed_societe_ids) ||
    JSON.stringify(allowedRoutes.sort()) !== JSON.stringify((permissions.allowed_routes || []).sort()) ||
    JSON.stringify(allowedSocieteIds.sort()) !== JSON.stringify((permissions.allowed_societe_ids || []).sort())
  );

  if (!user) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/20 z-[30] backdrop-blur-[1px]"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        data-testid="user-permissions-panel"
        className="fixed right-0 top-0 h-full w-full max-w-[520px] bg-white border-l border-zinc-200 z-[31] flex flex-col shadow-xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <div>
            <div className="text-sm font-semibold text-zinc-900">{user.name || user.email}</div>
            <div className="text-xs font-mono text-zinc-400">{user.email}</div>
          </div>
          <div className="flex items-center gap-3">
            {hasChanges && (
              <button
                data-testid="save-permissions-btn"
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-1.5 text-xs font-medium bg-[#002FA7] text-white px-3 py-1.5 hover:bg-[#00227A] transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                Enregistrer
              </button>
            )}
            <button onClick={onClose} className="text-zinc-400 hover:text-zinc-800 transition-colors">
              <X size={18} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-200 px-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`relative flex items-center gap-1.5 px-3 py-3 text-xs font-medium transition-colors whitespace-nowrap ${
                tab === t.key ? "text-[#002FA7]" : "text-zinc-500 hover:text-zinc-900"
              }`}
            >
              <t.icon size={12} strokeWidth={1.5} />
              {t.label}
              {tab === t.key && (
                <span className="absolute left-0 right-0 bottom-[-1px] h-[2px] bg-[#002FA7]" />
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 space-y-5">

          {/* ── PROFIL ── */}
          {tab === "profil" && (
            <div className="space-y-5">
              {/* Editable fields */}
              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-1.5">
                    Nom
                  </label>
                  <input
                    data-testid="edit-user-name"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    disabled={isSelf}
                    placeholder="Nom complet"
                    className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none disabled:bg-zinc-50 disabled:text-zinc-400"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-1.5">
                    Rôle
                  </label>
                  <select
                    data-testid="edit-user-role"
                    value={editRole}
                    onChange={(e) => setEditRole(e.target.value)}
                    disabled={isSelf}
                    className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none disabled:bg-zinc-50 disabled:text-zinc-400"
                  >
                    <option value="user">Utilisateur</option>
                    <option value="admin">Administrateur</option>
                  </select>
                </div>
              </div>

              {/* Read-only info */}
              <div className="space-y-3 border-t border-zinc-100 pt-4">
                <Field label="Email" value={user.email} mono />
                <Field label="E-mail vérifié" value={user.email_verified ? "Oui" : "En attente"} />
                <Field label="Membre depuis" value={formatDate(user.created_at)} />
                <Field label="Organisation" value={user.org_name || "—"} />
              </div>

              {!isSelf && (
                <>
                  <button
                    data-testid="save-profile-btn"
                    onClick={saveProfile}
                    disabled={savingProfile}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-medium py-2.5 transition-colors disabled:opacity-50"
                  >
                    {savingProfile ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                    Enregistrer le profil
                  </button>

                  {/* Delete zone */}
                  <div className="border border-red-100 bg-red-50/50 p-4 mt-2">
                    <div className="text-xs font-medium text-red-700 mb-2">Zone dangereuse</div>
                    <div className="text-xs text-zinc-500 mb-3">
                      La suppression est définitive. Les documents classés par cet utilisateur sont conservés.
                    </div>
                    <button
                      data-testid="delete-user-btn"
                      onClick={deleteUser}
                      disabled={deletingUser}
                      className="inline-flex items-center gap-2 border border-red-300 text-red-600 text-xs font-medium px-4 py-2 hover:bg-red-50 transition-colors disabled:opacity-50"
                    >
                      {deletingUser ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} strokeWidth={1.5} />}
                      Supprimer cet utilisateur
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── ACCÈS SOCIÉTÉS ── */}
          {tab === "acces" && (
            <div className="space-y-5">
              {loadingPerms ? (
                <div className="text-sm text-zinc-400 font-mono">Chargement…</div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-zinc-900">Accès aux sociétés</div>
                      <div className="text-xs text-zinc-500 mt-0.5">
                        Restreindre les sociétés et leurs documents accessibles par cet utilisateur.
                      </div>
                    </div>
                    <Toggle
                      checked={societeRestricted}
                      onChange={setSocieteRestricted}
                      testid="toggle-societe-restricted"
                    />
                  </div>

                  {societeRestricted && (
                    <div className="border border-zinc-200 divide-y divide-zinc-100">
                      {societes.length === 0 ? (
                        <div className="p-4 text-xs text-zinc-400">Aucune société configurée.</div>
                      ) : societes.map((s) => (
                        <div
                          key={s.id}
                          onClick={() => toggleSociete(s.id)}
                          className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-zinc-50 transition-colors"
                        >
                          <Checkbox
                            checked={allowedSocieteIds.includes(s.id)}
                            onChange={() => toggleSociete(s.id)}
                            testid={`societe-check-${s.id}`}
                          />
                          <span className="text-sm text-zinc-800">{s.nom}</span>
                          {s.siren && <span className="text-xs font-mono text-zinc-400 ml-auto">{s.siren}</span>}
                        </div>
                      ))}
                    </div>
                  )}

                  {!societeRestricted && (
                    <div className="border border-zinc-100 bg-zinc-50 px-4 py-3 text-xs text-zinc-500">
                      Accès complet à toutes les sociétés ({societes.length}).
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── DROITS MENU ── */}
          {tab === "menu" && (
            <div className="space-y-5">
              {loadingPerms ? (
                <div className="text-sm text-zinc-400 font-mono">Chargement…</div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-zinc-900">Accès aux pages</div>
                      <div className="text-xs text-zinc-500 mt-0.5">
                        Contrôle par URL. Les administrateurs ont toujours un accès complet.
                      </div>
                    </div>
                    <Toggle
                      checked={routeRestricted}
                      onChange={setRouteRestricted}
                      testid="toggle-route-restricted"
                    />
                  </div>

                  {routeRestricted && (
                    <div className="border border-zinc-200 divide-y divide-zinc-100">
                      {MENU_TREE.map((item) => (
                        <div
                          key={item.path}
                          onClick={() => toggleRoute(item.path)}
                          className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-zinc-50 transition-colors"
                        >
                          <Checkbox
                            checked={allowedRoutes.includes(item.path)}
                            onChange={() => toggleRoute(item.path)}
                            testid={`route-check-${item.path.replace(/\//g, "-") || "root"}`}
                          />
                          <item.icon size={14} strokeWidth={1.5} className="text-zinc-400 shrink-0" />
                          <div className="flex-1">
                            <div className="text-sm text-zinc-800">{item.label}</div>
                            <div className="text-[10px] font-mono text-zinc-400">{item.path}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {!routeRestricted && (
                    <div className="border border-zinc-100 bg-zinc-50 px-4 py-3 text-xs text-zinc-500">
                      Accès complet à toutes les pages ({MENU_TREE.length}).
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── CONNEXIONS ── */}
          {tab === "connexions" && (
            <div className="space-y-4">
              <div className="text-xs text-zinc-500">Historique des 30 derniers jours.</div>
              {loadingHistory ? (
                <div className="text-sm text-zinc-400 font-mono">Chargement…</div>
              ) : loginHistory.length === 0 ? (
                <div className="border border-zinc-100 bg-zinc-50 px-4 py-6 text-center text-xs text-zinc-400">
                  Aucune connexion enregistrée.
                </div>
              ) : (
                <div className="border border-zinc-200 divide-y divide-zinc-100">
                  {loginHistory.map((h) => (
                    <div key={h.id} data-testid="login-history-row" className="px-4 py-3 flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-emerald-400 mt-1.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-mono text-zinc-700">{formatDate(h.logged_at)}</div>
                        <div className="text-[11px] text-zinc-400 mt-0.5 flex gap-3 flex-wrap">
                          {h.ip_address && <span>IP : {h.ip_address.split(",")[0].trim()}</span>}
                          {h.user_agent && <span>{parseUA(h.user_agent)}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Field({ label, value, mono = false }) {
  return (
    <div className="flex items-baseline justify-between border-b border-zinc-100 pb-3">
      <span className="text-xs uppercase tracking-wider text-zinc-400 font-mono shrink-0 mr-4">{label}</span>
      <span className={`text-sm text-zinc-800 text-right ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

function Checkbox({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onChange}
      className={`w-4 h-4 border flex items-center justify-center shrink-0 transition-colors ${
        checked ? "bg-[#002FA7] border-[#002FA7]" : "border-zinc-300 hover:border-zinc-500"
      }`}
    >
      {checked && <Check size={10} strokeWidth={2.5} className="text-white" />}
    </button>
  );
}

function Toggle({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => onChange(!checked)}
      className={`relative w-10 h-5 rounded-full transition-colors ${
        checked ? "bg-[#002FA7]" : "bg-zinc-200"
      }`}
    >
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-5" : "translate-x-0"
      }`} />
    </button>
  );
}
